/**
 * React/TypeScript code for WebView to Flutter Authentication Bridge
 *
 * This file contains the code you need to paste into your React webview app
 * to communicate with the Flutter app for Google Sign-In authentication.
 *
 * Usage:
 * 1. Copy the FlutterAuthBridge component and use it in your app
 * 2. Call requestLogin() when you need to trigger the Google Sign-In flow
 * 3. Handle the returned Firebase ID token in your app
 */

import React, { useEffect, useState } from 'react';

/**
 * Hook to check if running in Flutter WebView
 */
export const useIsFlutterWebView = (): boolean => {
  const [isFlutterWebView, setIsFlutterWebView] = useState(false);

  useEffect(() => {
    // Check if flutter_inappwebview is available
    const checkFlutterWebView = () => {
      const isFlutter = typeof window !== 'undefined' && window.flutter_inappwebview !== undefined;
      setIsFlutterWebView(isFlutter);
    };

    checkFlutterWebView();

    // Also check after a short delay in case it loads asynchronously
    const timeout = setTimeout(checkFlutterWebView, 1000);

    return () => clearTimeout(timeout);
  }, []);

  return isFlutterWebView;
};

/** Options for requestFlutterLogin. Pass forceAccountChooser: true so Flutter shows the Google/Apple account picker. */
export type RequestFlutterLoginOptions = {
  forceAccountChooser?: boolean;
};

/**
 * Request Google Sign-In from Flutter app.
 * Returns the Firebase ID token if successful, null otherwise.
 * Pass forceAccountChooser: true so the Flutter app signs out from Google first and shows the account picker (allows switching accounts).
 */
export const requestFlutterLogin = async (options?: RequestFlutterLoginOptions): Promise<any> => {
  try {
    // Check if we're in a Flutter WebView
    if (!window.flutter_inappwebview) {
      console.warn('Not running in Flutter WebView. Login will not work.');
      return null;
    }

    console.log('🔐 Requesting login from Flutter app...', options);

    // Call the Flutter handler with options so Flutter can e.g. call GoogleSignIn().signOut() before signIn() to show account chooser
    const response = await window.flutter_inappwebview.callHandler('requestLogin', options ?? {});

    if (response.success && response.idToken) {
      console.log('✅ Login successful, received ID token');
      return response;
    } else {
      console.error('❌ Login failed:', response.error);
      return null;
    }
  } catch (error) {
    console.error('❌ Error calling Flutter login handler:', error);
    return null;
  }
};

/**
 * Tell the Flutter app to sign out from Google (and Apple) so the next sign-in shows the account picker.
 * Call this when the user signs out from the web app. Flutter should implement handler "requestSignOut"
 * and call GoogleSignIn().signOut() and Apple sign-out so the native plugins clear their cached account.
 */
export const requestFlutterSignOut = async (): Promise<void> => {
  if (typeof window === 'undefined' || !(window as any).flutter_inappwebview) {
    return;
  }
  try {
    await (window as any).flutter_inappwebview.callHandler('requestSignOut');
  } catch (e) {
    console.warn('Flutter requestSignOut failed (Flutter may not implement it yet):', e);
  }
};

/**
 * React component that provides authentication functionality
 * Use this in your app to trigger login
 */
export const FlutterAuthBridge: React.FC<{
  onLoginSuccess?: (user: any) => void;
  onLoginError?: (error: string) => void;
  children?: (props: {
    requestLogin: () => Promise<void>;
    isFlutterWebView: boolean;
    isLoading: boolean;
  }) => React.ReactNode;
}> = ({ onLoginSuccess, onLoginError, children }) => {
  const [isLoading, setIsLoading] = useState(false);
  const isFlutterWebView = useIsFlutterWebView();

  const handleLogin = async () => {
    if (!isFlutterWebView) {
      const error = 'Not running in Flutter WebView. Please use the mobile app.';
      console.warn(error);
      onLoginError?.(error);
      return;
    }

    setIsLoading(true);
    try {
      const user = await requestFlutterLogin({ forceAccountChooser: true });
      console.log(user);
      if (user) {
        onLoginSuccess?.(user);
      } else {
        onLoginError?.('Login was cancelled or failed');
      }
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Unknown error';
      onLoginError?.(errorMessage);
    } finally {
      setIsLoading(false);
    }
  };

  if (children) {
    return <>{children({ requestLogin: handleLogin, isFlutterWebView, isLoading })}</>;
  }

  return null;
};

/**
 * Example usage component
 */
export const LoginButton: React.FC<{
  onLoginSuccess: (idToken: string) => void;
}> = ({ onLoginSuccess }) => {
  return (
    <FlutterAuthBridge
      onLoginSuccess={onLoginSuccess}
      onLoginError={(error) => {
        console.error('Login error:', error);
        alert(`Login failed: ${error}`);
      }}
    >
      {({ requestLogin, isFlutterWebView, isLoading }) => (
        <button
          onClick={requestLogin}
          disabled={!isFlutterWebView || isLoading}
          style={{
            padding: '12px 24px',
            fontSize: 'var(--kol-button-font-size, 16px)',
            backgroundColor: isFlutterWebView ? '#4285f4' : '#ccc',
            color: 'white',
            border: 'none',
            borderRadius: '4px',
            cursor: isFlutterWebView && !isLoading ? 'pointer' : 'not-allowed',
          }}
        >
          {isLoading
            ? 'Signing in...'
            : isFlutterWebView
              ? 'Sign in with Google'
              : 'Please use mobile app'}
        </button>
      )}
    </FlutterAuthBridge>
  );
};

/**
 * Hook version for more flexible usage
 */
export const useFlutterGoogleAuth = () => {
  const [isLoading, setIsLoading] = useState(false);
  const isFlutterWebView = useIsFlutterWebView();

  const login = async (): Promise<any> => {
    if (!isFlutterWebView) {
      console.warn('Not running in Flutter WebView');
      return null;
    }

    setIsLoading(true);
    try {
      const user = await requestFlutterLogin({ forceAccountChooser: true });
      return user;
    } finally {
      setIsLoading(false);
    }
  };

  return {
    googleLogin: login,
    isLoading,
    isFlutterWebView,
  };
};
